import { CommonModule } from '@angular/common';
import { Component, computed, inject, input, output, signal } from '@angular/core';
import { Router } from '@angular/router';
import { GeofenceKindColor, GeofenceKindLabel, GeofenceV1Response, GroupedGeofence } from '@geofences/geofence';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';

import { BaseCard } from '@core/components/base-card/base-card';
import { GeofenceService } from '@core/services/geofence';
import { buildStaticMapUrl } from '@core/utils/static-map/static-map';
import { environment } from '@src/environments/environment';

@Component({
  selector: 'app-geofence-card',
  imports: [CommonModule, TagModule, ButtonModule, BaseCard],
  templateUrl: './geofence-card.html',
  styleUrls: ['./geofence-card.scss']
})
export class GeofenceCard {
  private readonly router = inject(Router);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly geofenceApis = inject(GeofenceService);
  private readonly messageService = inject(MessageService);

  readonly geofence = input.required<GroupedGeofence>();

  readonly deleted = output<void>();
  readonly isDeleting = signal(false);

  protected readonly GeofenceKindColor = GeofenceKindColor;

  readonly allGeofences = computed(() => {
    const gf = this.geofence();
    return [gf.hub, gf.delivery, gf.pickup].filter((g): g is GeofenceV1Response => !!g);
  });

  protected readonly previewFailed = signal(false);

  protected readonly mapPreviewUrl = computed(() =>
    buildStaticMapUrl({
      paths: this.allGeofences().map((g) => ({
        coordinates: g.coordinates,
        color: GeofenceKindColor[g.geofenceKindId]
      })),
      key: environment.googleMapsApiKey
    })
  );

  readonly primaryGeofence = computed(() => this.allGeofences()[0]);

  readonly availableTypes = computed(() =>
    this.allGeofences().map((gf) => ({
      kind: gf.geofenceKindId,
      label: GeofenceKindLabel[gf.geofenceKindId],
      geofence: gf
    }))
  );

  viewGeofenceDetails() {
    const primary = this.primaryGeofence();
    if (primary) {
      this.router.navigate(['/geofences/edit/', primary.geofenceId]);
    }
  }

  deleteGroup() {
    const gf = this.geofence();
    const count = this.allGeofences().length;
    const typeLabel = count > 1 ? `all ${count} geofences for` : '';

    this.confirmationService.confirm({
      message: `Are you sure you want to delete ${typeLabel} "${gf.name}"? This action cannot be undone.`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Delete',
      acceptButtonStyleClass: 'p-button-danger',
      rejectLabel: 'Cancel',
      rejectButtonStyleClass: 'p-button-secondary',
      accept: () => this.deleteAllInGroup()
    });
  }

  private async deleteAllInGroup(): Promise<void> {
    const geofenceIds = this.allGeofences().map((gf) => gf.geofenceId);
    const name = this.geofence().name;
    this.isDeleting.set(true);

    const results = await Promise.allSettled(geofenceIds.map((id) => this.geofenceApis.deleteById(id)));
    const failed = results.filter((r) => r.status === 'rejected');

    if (failed.length === geofenceIds.length) {
      this.showError('Failed to delete geofence(s). Please try again.');
    } else if (failed.length > 0) {
      this.showError(`Deleted with ${failed.length} failure(s). Please refresh.`);
      this.deleted.emit();
    } else {
      this.showSuccess(`"${name}" deleted successfully`);
      this.deleted.emit();
    }

    this.isDeleting.set(false);
  }

  private showSuccess(message: string): void {
    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: message
    });
  }

  private showError(message: string): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: message
    });
  }
}
